import type { ApiResponse } from "@qbanq/shared";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";
const TOKEN_KEY = "qbanq.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!json) throw new ApiError("network", "Invalid response", res.status);
  if (!json.ok) {
    if (res.status === 401) setToken(null);
    throw new ApiError(json.error.code, json.error.message, res.status);
  }
  return json.data;
}

/** Fetch a binary resource (e.g. media) with the bearer token; returns a Blob. */
async function getBlob(path: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    if (res.status === 401) setToken(null);
    throw new ApiError("media", `Media fetch failed (${res.status})`, res.status);
  }
  return res.blob();
}

/** GET that also returns the response `meta` (e.g. pagination cursor). */
async function getPage<T>(path: string): Promise<{ data: T; meta?: Record<string, unknown> }> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const json = (await res.json().catch(() => null)) as
    | { ok: true; data: T; meta?: Record<string, unknown> }
    | { ok: false; error: { code: string; message: string } }
    | null;
  if (!json) throw new ApiError("network", "Invalid response", res.status);
  if (!json.ok) {
    if (res.status === 401) setToken(null);
    throw new ApiError(json.error.code, json.error.message, res.status);
  }
  return { data: json.data, meta: json.meta };
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  getPage,
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>("POST", path, body, headers),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
  getBlob,
};
